import type { FallbackRender } from '@sentry/react';
import React, { createContext, useContext } from 'react';

/**
 * Surface-specific Sentry capture function. Called from `ScopedErrorBoundary`
 * when a child throws. Should return the Sentry event ID, or `null` if no
 * event was sent (e.g. no scope/client configured).
 */
export type ScopedCaptureFn = (
  error: unknown,
  errorInfo: React.ErrorInfo,
) => string | null;

const ScopedSentryContext = createContext<ScopedCaptureFn>(() => null);

/**
 * Provides a surface-specific `captureException` function to all
 * `<ScopedErrorBoundary>` instances rendered underneath. Mount once near the
 * top of each surface (e.g. extension content script, web-vite root) so every
 * boundary in the tree captures into the right Sentry client.
 */
export const ScopedSentryProvider: React.FC<{
  captureException: ScopedCaptureFn;
  children: React.ReactNode;
}> = ({ captureException, children }) => (
  <ScopedSentryContext.Provider value={captureException}>
    {children}
  </ScopedSentryContext.Provider>
);

type InnerProps = {
  fallback: FallbackRender;
  children: React.ReactNode;
  captureException: ScopedCaptureFn;
};

type State = {
  // Separate flag from `error` so a child throwing literal `null` (legal in
  // JS) still trips the boundary instead of looking identical to the
  // initial "no error yet" state.
  hasError: boolean;
  error: unknown;
  componentStack: string | null;
  eventId: string | null;
};

class ScopedErrorBoundaryClass extends React.Component<InnerProps, State> {
  state: State = {
    hasError: false,
    error: null,
    componentStack: null,
    eventId: null,
  };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const componentStack = errorInfo.componentStack ?? null;
    const eventId = this.props.captureException(error, errorInfo);
    this.setState({ componentStack, eventId });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      eventId: null,
    });
  };

  render() {
    const { hasError, error, componentStack, eventId } = this.state;
    if (hasError) {
      return this.props.fallback({
        error,
        componentStack: componentStack ?? '',
        eventId: eventId ?? '',
        resetError: this.resetError,
      });
    }
    return this.props.children;
  }
}

/**
 * React error boundary that reports caught errors via the `captureException`
 * function provided by the nearest `<ScopedSentryProvider>`. If no provider is
 * mounted up the tree, errors are caught and rendered in the fallback but not
 * sent to Sentry.
 *
 * This pattern lets a single boundary work across surfaces — the extension
 * content script uses a manually-configured BrowserClient + scoped Scope (per
 * Sentry's shared-environments guidance), while the web app uses
 * `Sentry.captureException`. Each surface wires its provider once.
 */
export const ScopedErrorBoundary: React.FC<{
  fallback: FallbackRender;
  children: React.ReactNode;
}> = (props) => {
  const captureException = useContext(ScopedSentryContext);
  return (
    <ScopedErrorBoundaryClass {...props} captureException={captureException} />
  );
};
